import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { useActiveMatch } from '../contexts/ActiveMatchContext';
import { usePlayerPopup } from '../contexts/PlayerPopupContext';
import { getRankInfo, RANK_COLORS, getTierBounds } from '../rank';
import MathText from '../components/MathText';

// ── Results modal helpers ─────────────────────────────────────────────────────
function AvatarFallback({ name, size = 56 }) {
  const initial = (name ?? '?').charAt(0).toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'var(--cb-navy)', border: '3px solid var(--cb-gray-200)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.36, fontWeight: 700, color: 'white',
    }}>
      {initial}
    </div>
  );
}

function OutcomeIcon({ type }) {
  const configs = {
    win:  { src: '/Victory_V2.png', height: 84 },
    loss: { src: '/Defeat_V2.png',  height: 73 },
    draw: { src: '/Tie_V2.png',     height: 75 },
  };
  const { src, height } = configs[type] ?? configs.draw;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 auto 12px', width: '100%' }}>
      <img src={src} alt={type} style={{ height, width: 'auto', display: 'block', margin: '0 auto' }} />
    </div>
  );
}

function useCountUp(target, duration = 1400) {
  const [display, setDisplay] = useState(0);
  const frameRef = useRef(null);
  useEffect(() => {
    if (target <= 0) { setDisplay(0); return; }
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * target));
      if (progress < 1) frameRef.current = requestAnimationFrame(step);
    };
    frameRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameRef.current);
  }, [target, duration]);
  return display;
}

function RankXpBar({ preXp, postXp, countUpDuration = 1200 }) {
  const rankInfo = getRankInfo(postXp);
  const { currentMin, nextMin } = getTierBounds(postXp);
  const tierSpan = nextMin > currentMin ? nextMin - currentMin : 1;

  const startFill = Math.min(100, Math.max(0, ((preXp - currentMin) / tierSpan) * 100));
  const endFill   = rankInfo.xpToNext > 0
    ? Math.min(100, Math.max(0, (rankInfo.xpIntoLevel / rankInfo.xpToNext) * 100))
    : 100;

  const [fill, setFill] = useState(startFill);
  const nextRankInfo = getRankInfo(rankInfo.minXp + (rankInfo.xpToNext || 0));

  useEffect(() => {
    setFill(startFill);
    const t = setTimeout(() => setFill(endFill), 50);
    return () => clearTimeout(t);
  }, [startFill, endFill]); // eslint-disable-line

  return (
    <div style={{ marginTop: 14 }}>
      {/* Top row: centered rank identity, XP readout on right */}
      <div className="rp-top-row" style={{ marginBottom: 8 }}>
        <div className="rp-identity" style={{ justifyContent: 'center' }}>
          <img src={rankInfo.icon} alt={rankInfo.label} className="rp-rank-icon" />
          <div className="rp-identity-text">
            <span className="rp-rank-label" style={{ color: rankInfo.color }}>{rankInfo.label}</span>
            <span className="rp-level-tag">Level {rankInfo.level}</span>
          </div>
        </div>
        <div className="rp-xp-readout">
          <span className="rp-xp-current" style={{ color: rankInfo.color }}>{rankInfo.xpIntoLevel.toLocaleString()}</span>
          <span className="rp-xp-denom"> / {rankInfo.xpToNext.toLocaleString()} XP</span>
        </div>
      </div>
      {/* Bar */}
      <div className="rp-bar-row">
        <img src={rankInfo.icon} alt={rankInfo.label} className="rp-ep-icon" title={rankInfo.label} />
        <div className="rp-bar-outer">
          <div className="rp-bar-track">
            <div
              className="rp-bar-fill"
              style={{
                width: `${fill}%`,
                background: rankInfo.color,
                transition: fill === startFill ? 'none' : 'width 1.4s cubic-bezier(0.4,0,0.2,1)',
              }}
            />
          </div>
        </div>
        <img src={nextRankInfo.icon} alt={nextRankInfo.label} className="rp-ep-icon" title={nextRankInfo.label} />
      </div>
      {/* Level labels */}
      <div className="rp-bar-labels" style={{ marginTop: 5 }}>
        <span style={{ color: rankInfo.color }}>Lvl {rankInfo.level}</span>
        <span style={{ color: nextRankInfo.color }}>Lvl {nextRankInfo.level}</span>
      </div>
    </div>
  );
}

function XpBreakdown({ totalXpGained, breakdown, preXp, postXp }) {
  const animatedTotal = useCountUp(totalXpGained, 1200);
  const itemColor = (item) => {
    if (item.type === 'resigned')  return 'var(--cb-gray-400)';
    if (item.type === 'questions') return '#22c55e';
    return '#4a90e2';
  };
  const itemLabel = (item) => {
    if (item.type === 'resigned')   return 'Resigned';
    if (item.label === 'win')         return 'Win bonus';
    if (item.label === 'loss')        return 'Loss bonus';
    if (item.label === 'tie')         return 'Tie bonus';
    if (item.label === 'time played') return 'Time played';
    if (item.type === 'questions')    return `${item.correctCount} correct answer${item.correctCount !== 1 ? 's' : ''}`;
    return item.label.charAt(0).toUpperCase() + item.label.slice(1);
  };
  return (
    <div style={{ paddingTop: 10, borderTop: '1px solid var(--cb-gray-200)', marginTop: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--cb-gray-500)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
        XP Breakdown
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {breakdown.map((item, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
            <span style={{ color: 'var(--cb-gray-600)' }}>{itemLabel(item)}</span>
            <span style={{ fontWeight: 700, color: itemColor(item) }}>+{item.amount} XP</span>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: '2px solid var(--cb-gray-200)' }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--cb-gray-700)' }}>Total XP</span>
        <span style={{ fontSize: 19, fontWeight: 900, color: '#4a90e2', fontVariantNumeric: 'tabular-nums' }}>
          +{animatedTotal} XP
        </span>
      </div>
      {preXp != null && postXp != null && (
        <RankXpBar preXp={preXp} postXp={postXp} countUpDuration={1200} />
      )}
    </div>
  );
}

const TOTAL = 12;

export default function Game() {
  const { gameId } = useParams();
  const { socket } = useSocket();
  const { user } = useAuth();
  const { setMatch, clearMatch } = useActiveMatch();
  const { openPopup } = usePlayerPopup();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  // The next question the player must answer in sequence (lowest unanswered index)
  const firstUnanswered = Array.from({ length: 12 }, (_, i) => i).find(i => answers[i] === undefined) ?? 12;
  const [revealed, setRevealed] = useState({});
  const [myScore, setMyScore] = useState(0);
  const [oppScore, setOppScore] = useState(0);
  const [myProgress, setMyProgress] = useState(0);
  const [oppProgress, setOppProgress] = useState(0);
  const [oppCurrentQuestion, setOppCurrentQuestion] = useState(-1);
  const [opponent, setOpponent] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [timeLeft, setTimeLeft] = useState(15 * 60);
  const [gameEnded, setGameEnded] = useState(false);
  const [gameResults, setGameResults] = useState(null);
  const [waitingForOpponent, setWaitingForOpponent] = useState(true);
  const [resignConfirm, setResignConfirm] = useState(false);
  const [timerCutLabel, setTimerCutLabel] = useState(null); // "-Xm Ys" flash text
  const [timerCutFlash, setTimerCutFlash] = useState(false); // brief red flash on timer
  const timerRef = useRef(null);
  const timerCutLabelRef = useRef(null);

  // ── Chat state ──────────────────────────────────────────────────────────────
  const [chatMessages, setChatMessages]     = useState([]);
  const [chatInput, setChatInput]           = useState('');
  const [chatError, setChatError]           = useState('');
  const chatEndRef                          = useRef(null);
  const chatErrorTimerRef                   = useRef(null);

  useEffect(() => {
    const cached = sessionStorage.getItem(`gamedata_${gameId}`);
    if (cached) {
      try {
        const data = JSON.parse(cached);
        setQuestions(data.questions || []);
        setOpponent(data.opponent || null);
        setEndTime(data.endTime);
        setWaitingForOpponent(false);
      } catch {}
    }
  }, [gameId]);

  useEffect(() => {
    if (!socket) return;

    const handleStart = (data) => {
      setQuestions(data.questions || []);
      setOpponent(data.opponent || null);
      setEndTime(data.endTime);
      setWaitingForOpponent(false);
      setChatMessages(data.chatHistory || []);
      setMatch({ gameId, endTime: data.endTime }); // register active match
    };

    const handleReconnect = (data) => {
      // server sends { gameId, game: { questions, opponent, ... } }
      // unwrap the nested game object; fall back to flat data for safety
      const g = data.game ?? data;
      setQuestions(g.questions || []);
      setOpponent(g.opponent || null);
      setEndTime(g.endTime);
      setMyScore(g.myScore ?? 0);
      setOppScore(g.opponentScore ?? 0);
      setOppProgress(g.opponentProgress ?? 0);
      if (g.opponentLastAnswered !== undefined) {
        setOppCurrentQuestion(g.opponentLastAnswered);
      }
      const restoredAnswers = g.myAnswers || {};
      setAnswers(restoredAnswers);
      setMyProgress(Object.keys(restoredAnswers).length);
      // Jump to the first unanswered question instead of staying on Q1
      const firstUnansweredIdx = Array.from({ length: TOTAL }, (_, i) => i).find(i => restoredAnswers[i] === undefined);
      setCurrent(firstUnansweredIdx ?? 0);
      setWaitingForOpponent(false);
      setChatMessages(g.chatHistory || []);
      setMatch({ gameId, endTime: g.endTime }); // re-confirm active match
    };

    const handleAnswerResult = ({ questionIndex, correct, correctAnswer }) => {
      setRevealed(prev => ({ ...prev, [questionIndex]: { correct, correctAnswer } }));
      if (correct) setMyScore(s => s + 1);
      setMyProgress(p => p + 1);
    };

    const handleScoreUpdate = (data) => {
      setOppProgress(data.opponentProgress ?? data.player2Progress ?? 0);
      setOppScore(data.opponentScore ?? data.player2Score ?? 0);
      if (data.opponentLastAnswered !== undefined) {
        setOppCurrentQuestion(data.opponentLastAnswered);
      }
    };

    const handleEnd = (results) => {
      setGameEnded(true);
      clearInterval(timerRef.current);
      clearMatch(); // release route guard
      sessionStorage.setItem(`results_${gameId}`, JSON.stringify(results));
      sessionStorage.removeItem(`gamedata_${gameId}`);
      setGameResults(results);
    };

    // Server tells us the game no longer exists (e.g. navigated directly to /game/:id
    // after the game already ended — redirect home rather than hang on loading spinner)
    const handleEnded = () => { clearMatch(); navigate('/'); };

    const handleTimerAdjusted = ({ newEndTime, savedMs }) => {
      setEndTime(newEndTime);
      const totalSec = Math.round(savedMs / 1000);
      const mins = Math.floor(totalSec / 60);
      const secs = totalSec % 60;
      const label = mins > 0
        ? `-${mins}m ${secs > 0 ? secs + 's' : ''}`
        : `-${secs}s`;
      // Clear any previous label timer
      if (timerCutLabelRef.current) clearTimeout(timerCutLabelRef.current);
      setTimerCutLabel(label.trim());
      setTimerCutFlash(true);
      timerCutLabelRef.current = setTimeout(() => {
        setTimerCutLabel(null);
        setTimerCutFlash(false);
      }, 3400);
    };

    // ── Chat events ──────────────────────────────────────────────────────────
    const handleChatMessage = (msg) => {
      setChatMessages(prev => [...prev, msg]);
    };

    const handleChatError = ({ text }) => {
      setChatError(text || 'Message blocked.');
      if (chatErrorTimerRef.current) clearTimeout(chatErrorTimerRef.current);
      chatErrorTimerRef.current = setTimeout(() => setChatError(''), 4500);
    };

    socket.on('game:start', handleStart);
    socket.on('game:reconnect', handleReconnect);
    socket.on('game:answerResult', handleAnswerResult);
    socket.on('game:scoreUpdate', handleScoreUpdate);
    socket.on('game:end', handleEnd);
    socket.on('game:ended', handleEnded);
    socket.on('game:timerAdjusted', handleTimerAdjusted);
    socket.on('chat:message', handleChatMessage);
    socket.on('chat:error', handleChatError);

    // Emit rejoin so the server updates its stored socketId for this player.
    // This is essential after a hard-refresh: the socket reconnects with a new ID
    // but the game object still references the old one, so without this emit,
    // future game:end / game:answerResult events would go to a dead socket.
    socket.emit('game:rejoin', { gameId });

    return () => {
      socket.off('game:start', handleStart);
      socket.off('game:reconnect', handleReconnect);
      socket.off('game:answerResult', handleAnswerResult);
      socket.off('game:scoreUpdate', handleScoreUpdate);
      socket.off('game:timerAdjusted', handleTimerAdjusted);
      socket.off('game:end', handleEnd);
      socket.off('game:ended', handleEnded);
      socket.off('chat:message', handleChatMessage);
      socket.off('chat:error', handleChatError);
      if (chatErrorTimerRef.current) clearTimeout(chatErrorTimerRef.current);
      if (timerCutLabelRef.current) clearTimeout(timerCutLabelRef.current);
    };
  }, [socket, gameId, navigate, setMatch, clearMatch]);

  useEffect(() => {
    if (!endTime) return;
    const tick = () => {
      const remaining = Math.max(0, Math.round((endTime - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining === 0) clearInterval(timerRef.current);
    };
    tick();
    timerRef.current = setInterval(tick, 500);
    return () => clearInterval(timerRef.current);
  }, [endTime]);

  const submitAnswer = useCallback((letter) => {
    if (answers[current] !== undefined || gameEnded) return;
    setAnswers(prev => ({ ...prev, [current]: letter }));
    socket?.emit('game:answer', { gameId, questionIndex: current, answer: letter });

    setTimeout(() => {
      setCurrent(prev => {
        let next = prev + 1;
        while (next < TOTAL && answers[next] !== undefined) next++;
        return next < TOTAL ? next : prev;
      });
    }, 400);
  }, [answers, current, gameEnded, gameId, socket]);

  const handleResign = () => {
    if (gameEnded) return;
    setResignConfirm(true);
  };

  const confirmResign = () => {
    setResignConfirm(false);
    socket?.emit('game:resign', { gameId });
  };

  // Auto-scroll chat to bottom whenever a new message arrives
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [chatMessages]);

  const sendChatMessage = useCallback(() => {
    const trimmed = chatInput.trim();
    if (!trimmed || gameEnded) return;
    socket?.emit('chat:message', { gameId, message: trimmed });
    setChatInput('');
  }, [chatInput, gameEnded, gameId, socket]);

  useEffect(() => {
    const handler = (e) => {
      // Never intercept keys while the user is typing in the chat input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const letter = e.key.toUpperCase();
      if (['A', 'B', 'C', 'D'].includes(letter)) submitAnswer(letter);

      // Arrow navigation obeys the same rule as the nav buttons:
      // only reachable if the question is already answered or is the first unanswered.
      const canVisit = (idx) => idx >= 0 && idx < TOTAL &&
        (answers[idx] !== undefined || idx === firstUnanswered);

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        setCurrent(c => { const n = c + 1; return canVisit(n) ? n : c; });
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        setCurrent(c => { const n = c - 1; return canVisit(n) ? n : c; });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [submitAnswer, answers, firstUnanswered]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const timerClass = timeLeft <= 60 ? 'danger' : timeLeft <= 180 ? 'warning' : '';
  const progressPct = endTime ? Math.max(0, (endTime - Date.now()) / (15 * 60 * 1000)) * 100 : 100;
  const answeredCount = Object.keys(answers).length;

  if (waitingForOpponent || questions.length === 0) {
    return (
      <div className="game-shell" style={{ alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
        <div className="queue-spinner" />
        <p style={{ marginTop: 16, color: 'var(--cb-gray-500)' }}>Loading game…</p>
      </div>
    );
  }

  const q = questions[current];

  if (!q || !q.choices) {
    return (
      <div className="game-shell" style={{ alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
        <div className="queue-spinner" />
        <p style={{ marginTop: 16, color: 'var(--cb-gray-500)' }}>Loading question…</p>
      </div>
    );
  }

  const isRW = q.section === 'Reading & Writing';
  const rev = revealed[current];
  const myAnswer = answers[current];

  // oppCurrentQuestion = the last question index the opponent answered.
  // We show the pip on the *next* question (the one they're currently on).
  // At match start (oppCurrentQuestion === -1) we show them on question 0.
  const oppPipIdx = oppCurrentQuestion + 1;

  return (
    <div className="game-shell">

      {/* ── Results modal ─────────────────────────────────────────────────── */}
      {gameResults && (() => {
        const { isWinner, myScore, opponentScore, expGain, expBreakdown, winner, reason } = gameResults;
        const isDraw = !winner;
        const outcomeType = isDraw ? 'draw' : isWinner ? 'win' : 'loss';
        const isP1 = gameResults.player1.userId === user?.id;
        const meP  = isP1 ? gameResults.player1 : gameResults.player2;
        const oppP = isP1 ? gameResults.player2 : gameResults.player1;
        const meRank  = getRankInfo(meP.newRating  != null ? meP.newRating  - (meP.ratingDelta  ?? meP.expGain  ?? 0) : 0);
        const oppRank = getRankInfo(oppP.newRating != null ? oppP.newRating - (oppP.ratingDelta ?? oppP.expGain ?? 0) : 0);
        const totalXpGained = expGain ?? meP.expGain ?? 0;
        const breakdown = expBreakdown ?? meP.expBreakdown ?? [];
        const subtitle = reason === 'resign'
          ? (isWinner ? 'Opponent resigned' : 'You resigned')
          : reason === 'forfeit' ? 'Opponent disconnected'
          : reason === 'timeout' ? 'Time expired'
          : 'Match complete';

        return (
          <div style={{
            position: 'fixed', top: 60, left: 0, right: 0, bottom: 0,
            background: 'rgba(8,14,28,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 99, overflowY: 'auto', padding: '20px 16px',
          }}>
            <div style={{
              background: 'var(--cb-white)',
              borderRadius: 16,
              padding: '36px 32px 28px',
              maxWidth: 500, width: '92%',
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
              border: '1px solid var(--cb-gray-200)',
              animation: 'modalPop 0.28s cubic-bezier(.34,1.56,.64,1)',
            }}>
              <OutcomeIcon type={outcomeType} />
              <div style={{ fontSize: 14, color: 'var(--cb-gray-500)', marginBottom: 18 }}>{subtitle}</div>

              {/* Score row */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
                background: 'var(--cb-gray-100)', borderRadius: 12, padding: '18px 12px', marginBottom: 4,
              }}>
                {/* Me — avatar (with crown if winner) + name */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, flex: 1, minWidth: 0 }}>
                  <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                    {!isDraw && isWinner && (
                      <img src="/Crown4.png" alt="crown" style={{ width: 26, height: 26, objectFit: 'contain', marginRight: 4, flexShrink: 0 }} />
                    )}
                    {meP.avatar
                      ? <img src={meP.avatar} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--cb-gray-200)' }} />
                      : <AvatarFallback name={meP.name} size={44} />
                    }
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, maxWidth: 110, overflow: 'hidden' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0,
                      background: `${meRank.color}20`, border: `1.5px solid ${meRank.color}55`,
                      borderRadius: 5, padding: '1px 5px', fontSize: 10, fontWeight: 800, color: meRank.color,
                    }}>
                      {meRank.level}
                      <img src={meRank.icon} alt={meRank.label} style={{ width: 12, height: 12 }} />
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--cb-gray-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{meP.name ?? 'You'}</span>
                  </div>
                </div>

                {/* Scores flanking the hyphen */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  <div style={{ fontSize: 38, fontWeight: 800, lineHeight: 1, minWidth: 32, textAlign: 'right', color: 'var(--cb-navy-text)' }}>{myScore}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--cb-gray-300)' }}>–</div>
                  <div style={{ fontSize: 38, fontWeight: 800, lineHeight: 1, minWidth: 32, textAlign: 'left', color: 'var(--cb-navy-text)' }}>{opponentScore}</div>
                </div>

                {/* Opponent — avatar (with crown if winner) + name */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, flex: 1, minWidth: 0 }}>
                  <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                    {oppP.avatar
                      ? <img src={oppP.avatar} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--cb-gray-200)' }} />
                      : <AvatarFallback name={oppP.name} size={44} />
                    }
                    {!isDraw && !isWinner && (
                      <img src="/Crown4.png" alt="crown" style={{ width: 26, height: 26, objectFit: 'contain', marginLeft: 4, flexShrink: 0 }} />
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, maxWidth: 110, overflow: 'hidden' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0,
                      background: `${oppRank.color}20`, border: `1.5px solid ${oppRank.color}55`,
                      borderRadius: 5, padding: '1px 5px', fontSize: 10, fontWeight: 800, color: oppRank.color,
                    }}>
                      {oppRank.level}
                      <img src={oppRank.icon} alt={oppRank.label} style={{ width: 12, height: 12 }} />
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--cb-gray-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{oppP.name ?? 'Opponent'}</span>
                  </div>
                </div>
              </div>

              {/* XP breakdown */}
              <XpBreakdown
                totalXpGained={totalXpGained}
                breakdown={breakdown}
                postXp={meP.newRating ?? 0}
                preXp={(meP.newRating ?? 0) - totalXpGained}
              />

              {/* Actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 22 }}>
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => navigate('/queue')}>
                  Play Again
                </button>
                <button
                  className="btn btn-outline"
                  style={{ width: '100%' }}
                  onClick={() => navigate(`/results/${gameId}`)}
                >
                  Review Answers
                </button>
                <div style={{ display: 'flex', gap: 10 }}>
                  <Link to="/leaderboard" className="btn btn-ghost" style={{ flex: 1 }}>Leaderboard</Link>
                  <Link to="/" className="btn btn-ghost" style={{ flex: 1 }}>Home</Link>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Resign confirmation modal */}
      {resignConfirm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: 'var(--cb-white)', borderRadius: 14, padding: '28px 32px',
            maxWidth: 380, width: '90%', textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--cb-navy)', marginBottom: 8 }}>
              Resign Match?
            </div>
            <div style={{ fontSize: 14, color: 'var(--cb-gray-500)', marginBottom: 24, lineHeight: 1.5 }}>
              Your opponent will be declared the winner and receive rating points.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button className="btn btn-outline" onClick={() => setResignConfirm(false)}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmResign}>
                Resign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top Bar */}
      <div className="game-topbar">
        <div className="score-panel">
          {(() => {
            const myRank = getRankInfo(user?.rating ?? 0);
            const oppRank = getRankInfo(opponent?.rating ?? 0);
            return (
              <>
                {/* Left player info */}
                <div className="score-player score-player-left">
                  <div className="score-name-row">
                    <button
                      onClick={e => user?.id && openPopup(user.id, e.clientX, e.clientY)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
                    >
                      {user?.avatar
                        ? <img src={user.avatar} alt="" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${myRank.color}88`, flexShrink: 0 }} />
                        : <div style={{ width: 30, height: 30, borderRadius: '50%', background: myRank.color + '33', border: `2px solid ${myRank.color}88`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: myRank.color, flexShrink: 0 }}>
                            {(user?.display_name || user?.name || '?').charAt(0).toUpperCase()}
                          </div>
                      }
                    </button>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 3,
                      background: `${myRank.color}22`, border: `1.5px solid ${myRank.color}55`,
                      borderRadius: 6, padding: '2px 6px', fontSize: 11, fontWeight: 800,
                      color: myRank.color, flexShrink: 0,
                    }}>
                      {myRank.level}
                      <img src={myRank.icon} alt={myRank.label} title={myRank.label} style={{ width: 16, height: 16, objectFit: 'contain' }} />
                    </span>
                    <button
                      onClick={e => user?.id && openPopup(user.id, e.clientX, e.clientY)}
                      className="score-name"
                      style={{ color: myRank.color, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                    >
                      {(user?.display_name || user?.name)?.split(' ')[0]}
                    </button>
                  </div>
                  <div className="score-school">{user?.school || '\u00A0'}</div>
                </div>

                {/* VS divider */}
                <div className="score-vs-block">
                  <div className="score-vs">VS</div>
                </div>

                {/* Right player info */}
                <div className="score-player score-player-right">
                  <div className="score-name-row">
                    <button
                      onClick={e => opponent?.userId && openPopup(opponent.userId, e.clientX, e.clientY)}
                      className="score-name"
                      style={{ color: oppRank.color, background: 'none', border: 'none', padding: 0, cursor: opponent?.userId ? 'pointer' : 'default' }}
                    >
                      {opponent?.name?.split(' ')[0] ?? '?'}
                    </button>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 3,
                      background: `${oppRank.color}22`, border: `1.5px solid ${oppRank.color}55`,
                      borderRadius: 6, padding: '2px 6px', fontSize: 11, fontWeight: 800,
                      color: oppRank.color, flexShrink: 0,
                    }}>
                      {oppRank.level}
                      <img src={oppRank.icon} alt={oppRank.label} title={oppRank.label} style={{ width: 16, height: 16, objectFit: 'contain' }} />
                    </span>
                    <button
                      onClick={e => opponent?.userId && openPopup(opponent.userId, e.clientX, e.clientY)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: opponent?.userId ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
                    >
                      {opponent?.avatar
                        ? <img src={opponent.avatar} alt="" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${oppRank.color}88`, flexShrink: 0 }} />
                        : <div style={{ width: 30, height: 30, borderRadius: '50%', background: oppRank.color + '33', border: `2px solid ${oppRank.color}88`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: oppRank.color, flexShrink: 0 }}>
                            {(opponent?.name || '?').charAt(0).toUpperCase()}
                          </div>
                      }
                    </button>
                  </div>
                  <div className="score-school">{opponent?.school || '\u00A0'}</div>
                </div>
              </>
            );
          })()}
        </div>

        <div className="game-topbar-center">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className={`game-timer ${timerClass}${timerCutFlash ? ' timer-cut-flash' : ''}`}>{formatTime(timeLeft)}</div>
            {timerCutLabel && (
              <span key={timerCutLabel} className="timer-cut-label">{timerCutLabel}</span>
            )}
          </div>
          <div className="game-timer-label">Time Remaining</div>
          <div className="game-progress-bar">
            <div className="game-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        <div style={{ textAlign: 'right', minWidth: 100 }}>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,.7)' }}>
            {answeredCount}/{TOTAL} answered
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginTop: 2 }}>
            Q{current + 1} of {TOTAL}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="game-body">

        {/* Sidebar */}
        <div className="question-sidebar">
          <div className="sidebar-section">Reading &amp; Writing</div>
          <div className="question-nav-grid">
            {Array.from({ length: 6 }, (_, i) => (
              <button
                key={i}
                className={`qnav-btn${current === i ? " active" : ""}${answers[i] !== undefined ? (" answered") : ""}`}
                onClick={() => { if (answers[i] !== undefined || i === firstUnanswered) setCurrent(i); }}
                style={{ position: 'relative', ...(answers[i] === undefined && i !== firstUnanswered ? { opacity: 0.45, cursor: 'default' } : {}) }}
              >
                {i + 1}
                {oppPipIdx === i && (
                  opponent?.avatar
                    ? <img src={opponent.avatar} alt="" style={{
                        position: 'absolute', top: -7, right: -7,
                        width: 18, height: 18, borderRadius: '50%',
                        border: '2px solid white', objectFit: 'cover',
                        boxShadow: '0 1px 4px rgba(0,0,0,.3)', zIndex: 2
                      }} />
                    : <span style={{
                        position: 'absolute', top: -7, right: -7,
                        width: 18, height: 18, borderRadius: '50%',
                        border: '2px solid white', background: 'var(--cb-navy)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 7, fontWeight: 800, color: 'white',
                        boxShadow: '0 1px 4px rgba(0,0,0,.3)', zIndex: 2
                      }}>
                        {(opponent?.name ?? '?').charAt(0).toUpperCase()}
                      </span>
                )}
              </button>
            ))}
          </div>

          <div className="sidebar-section">Math</div>
          <div className="question-nav-grid">
            {Array.from({ length: 6 }, (_, i) => {
              const idx = i + 6;
              return (
                <button
                  key={idx}
                  className={`qnav-btn${current === idx ? ' active' : ''}${answers[idx] !== undefined ? ' answered' : ''}`}
                  onClick={() => { if (answers[idx] !== undefined || idx === firstUnanswered) setCurrent(idx); }}
                  style={{ position: 'relative', ...(answers[idx] === undefined && idx !== firstUnanswered ? { opacity: 0.45, cursor: 'default' } : {}) }}
                >
                  {i + 1}
                  {oppPipIdx === idx && (
                    opponent?.avatar
                      ? <img src={opponent.avatar} alt="" style={{
                          position: 'absolute', top: -7, right: -7,
                          width: 18, height: 18, borderRadius: '50%',
                          border: '2px solid white', objectFit: 'cover',
                          boxShadow: '0 1px 4px rgba(0,0,0,.3)', zIndex: 2
                        }} />
                      : <span style={{
                          position: 'absolute', top: -7, right: -7,
                          width: 18, height: 18, borderRadius: '50%',
                          border: '2px solid white', background: 'var(--cb-navy)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 7, fontWeight: 800, color: 'white',
                          boxShadow: '0 1px 4px rgba(0,0,0,.3)', zIndex: 2
                        }}>
                          {(opponent?.name ?? '?').charAt(0).toUpperCase()}
                        </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* ── Match Chat ─────────────────────────────────────────────────── */}
          <div className="match-chat">
            <div className="match-chat-header">Match Chat</div>
            <div className="match-chat-feed">
              {chatMessages.length === 0 ? (
                <div className="match-chat-empty">No messages yet — say hi!</div>
              ) : (
                chatMessages.map(msg => {
                  if (msg.system && msg.xp) {
                    const isTimerAlert = !!msg.timerAlert;
                    return (
                      <div key={msg.id} className="match-chat-msg match-chat-xp">
                        <span style={{ color: isTimerAlert ? '#ff4444' : '#4a90e2', fontWeight: 700, fontSize: 12 }}>
                          ✦ {msg.message}
                        </span>
                      </div>
                    );
                  }
                  const msgRankInfo = getRankInfo(msg.rating ?? 0);
                  return (
                    <div key={msg.id} className="match-chat-msg">
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 2,
                        background: `${msgRankInfo.color}18`, border: `1px solid ${msgRankInfo.color}44`,
                        borderRadius: 4, padding: '0px 4px', fontSize: 9, fontWeight: 800,
                        color: msgRankInfo.color, flexShrink: 0, lineHeight: 1.5,
                      }}>
                        {msgRankInfo.level}
                        <img src={msgRankInfo.icon} alt={msgRankInfo.label} className="chat-rank-icon" style={{ margin: 0, width: 12, height: 12 }} />
                      </span>
                      <span className="match-chat-user" style={{ color: msgRankInfo.color }}>
                        {(msg.name || '?').split(' ')[0]}
                      </span>
                      <span className="match-chat-text">: {msg.message}</span>
                    </div>
                  );
                })
              )}
              <div ref={chatEndRef} />
            </div>

            {chatError && (
              <div className="match-chat-error" role="alert">{chatError}</div>
            )}

            <div className="match-chat-input-row">
              <input
                className="match-chat-input"
                type="text"
                placeholder={gameEnded ? 'Game over' : 'Message…'}
                value={chatInput}
                maxLength={200}
                disabled={gameEnded}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendChatMessage();
                  }
                }}
              />
              <button
                className="match-chat-send"
                onClick={sendChatMessage}
                disabled={!chatInput.trim() || gameEnded}
                aria-label="Send message"
              >
                ➤
              </button>
            </div>
          </div>

          {!gameEnded && (
            <div style={{ padding: '12px 14px', marginTop: 'auto', borderTop: '1px solid var(--cb-gray-200)' }}>
              <button
                className="btn btn-danger btn-sm"
                style={{ width: '100%' }}
                onClick={handleResign}
              >
                Resign
              </button>
            </div>
          )}
        </div>

        {/* Main Question Area */}
        <div className="question-main">
          <div className="question-header">
            <span className="question-number">Question {current + 1}</span>
            <span className={`question-section-badge ${isRW ? '' : 'math'}`}>{q.section}</span>
            {q.domain && <span className="question-domain">{q.domain}</span>}
          </div>

          {q.figures && q.figures.length > 0 ? (
            q.figures.map((fig, i) => (
              <img
                key={i}
                src={`/figures/${fig}`}
                alt={`Question figure ${i + 1}`}
                style={{ maxWidth: '100%', marginBottom: 10, borderRadius: 8, border: '1px solid var(--cb-gray-200)', display: 'block' }}
              />
            ))
          ) : q.figure ? (
            <img
              src={`/figures/${q.figure}`}
              alt="Question figure"
              style={{ maxWidth: '100%', marginBottom: 14, borderRadius: 8, border: '1px solid var(--cb-gray-200)', display: 'block' }}
            />
          ) : null}

          {q.notes && Array.isArray(q.notes) ? (
            <div className="passage-box">
              <div className="passage-label">Notes</div>
              <ul className="notes-list">
                {q.notes.map((n, i) => <li key={i}><MathText text={n} /></li>)}
              </ul>
            </div>
          ) : q.passage ? (
            <div className="passage-box">
              <div className="passage-label">{isRW ? 'Passage' : 'Problem'}</div>
              <MathText text={q.passage} />
            </div>
          ) : null}

          <div className="question-text"><MathText text={q.question} /></div>

          <div className="choices-list">
            {Object.entries(q.choices).map(([letter, text]) => {
              let cls = '';
              if (myAnswer === letter) {
                cls = rev ? (rev.correct ? 'correct' : 'incorrect') : 'selected';
              } else if (rev && !rev.correct && letter === rev.correctAnswer) {
                cls = 'correct';
              }
              return (
                <button
                  key={letter}
                  className={`choice-btn ${cls}`}
                  disabled={myAnswer !== undefined}
                  onClick={() => submitAnswer(letter)}
                >
                  <span className="choice-label">{letter}</span>
                  <MathText text={text} />
                </button>
              );
            })}
          </div>

          {myAnswer === undefined && (
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--cb-gray-500)' }}>
              Press{' '}
              {['A', 'B', 'C', 'D'].map(k => (
                <kbd key={k} style={{ background: 'var(--cb-gray-200)', padding: '1px 5px', borderRadius: 3, fontSize: 11, marginRight: 3 }}>{k}</kbd>
              ))}
              {' '}to answer · Arrow keys to navigate
            </div>
          )}

          <div className="question-nav-btns">
            <button className="btn btn-outline btn-sm" disabled={current === 0} onClick={() => setCurrent(c => c - 1)}>
              ← Previous
            </button>
            <span className="question-progress-text">{answeredCount} of {TOTAL} answered</span>
            <button className="btn btn-outline btn-sm" disabled={current === TOTAL - 1} onClick={() => setCurrent(c => c + 1)}>
              Next →
            </button>
          </div>

          {answeredCount === TOTAL && (
            <div style={{ marginTop: 20, padding: '16px 20px', background: 'var(--cb-green-light)', border: '1px solid var(--cb-green)', borderRadius: 8, textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--cb-green)' }}>
                All questions answered!
              </div>
              <div style={{ fontSize: 13, color: 'var(--cb-green)', marginTop: 4 }}>
                Waiting for your opponent to finish or the timer to expire…
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
